import { FormEvent, useState } from 'react'
import { StickyNote, Paperclip } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import AttachmentPreview from '@/components/AttachmentPreview'
import { useInboxContext } from './InboxContext'
import { useAttachmentInput } from '@/hooks/inbox/useAttachmentInput'
import type { PendingAttachment } from './types'

export default function NoteInputArea() {
  const { onSendNote } = useInboxContext()
  const [body, setBody] = useState('')
  const [notePendingAttachments, setNotePendingAttachments] = useState<PendingAttachment[]>([])

  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform)
  const modifierKey = isMac ? '⌘' : 'Ctrl'

  const {
    fileInputRef,
    isDragOver,
    handleFileSelect,
    handleRemoveAttachment,
    handlePasteFiles,
    dragHandlers,
  } = useAttachmentInput({
    setPendingAttachments: setNotePendingAttachments,
  })

  const canSubmit = body.trim().length > 0 || notePendingAttachments.length > 0

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    const files = notePendingAttachments.length > 0
      ? notePendingAttachments.map(a => a.file)
      : undefined
    onSendNote(body, files)
    setBody('')
    setNotePendingAttachments([])
  }

  function handlePaste(e: React.ClipboardEvent) {
    const files = Array.from(e.clipboardData.files)
    if (files.length > 0) {
      e.preventDefault()
      handlePasteFiles(files)
    }
  }

  return (
    <div className="relative" {...dragHandlers}>
      {isDragOver && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-amber-50/80 border-2 border-dashed border-amber-400 rounded-xl">
          <span className="text-sm font-medium text-amber-600">Soltar arquivos aqui</span>
        </div>
      )}
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          onChange={handleFileSelect}
          className="hidden"
        />

        {notePendingAttachments.length > 0 && (
          <AttachmentPreview
            attachments={notePendingAttachments}
            onRemove={handleRemoveAttachment}
          />
        )}

        <div className="flex gap-2">
          <textarea
            className="flex-1 px-3 py-2 text-sm bg-amber-50 border border-amber-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            rows={4}
            placeholder="Adicionar nota interna (visível apenas para agentes)..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onPaste={handlePaste}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                if (canSubmit) e.currentTarget.form?.requestSubmit()
              }
            }}
          />
          <div className="flex flex-col gap-1 self-end">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-2 text-amber-500 hover:text-amber-700 hover:bg-amber-100 rounded-xl transition-colors"
                >
                  <Paperclip className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">Anexar arquivo</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="px-3 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <StickyNote className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">Adicionar nota ({modifierKey} + Enter)</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </form>
    </div>
  )
}
